import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import GroupArchiveModal from "../../../../../components/dashboardcomponents/People/Groups/GroupArchiveModal";
import GroupActivateModal from "../../../../../components/dashboardcomponents/People/Groups/GroupActivateModal";
import GroupFormModal from "../../../../../components/dashboardcomponents/People/Groups/GroupFormModal";
import GroupDetailHeaderBand from "../../../../../components/dashboardcomponents/People/Groups/GroupDetail/GroupDetailHeaderBand";
import GroupDetailStatsRow from "../../../../../components/dashboardcomponents/People/Groups/GroupDetail/GroupDetailStatsRow";
import GroupDetailInfoSection from "../../../../../components/dashboardcomponents/People/Groups/GroupDetail/GroupDetailInfoSection";
import GroupDetailTeamsSection from "../../../../../components/dashboardcomponents/People/Groups/GroupDetail/GroupDetailTeamsSection";
import {
  buildGroupDetailView,
} from "../../../../../components/dashboardcomponents/People/Groups/GroupDetail/groupDetailHelpers";
import {
  GROUP_DETAIL_ARCHIVE,
  GROUP_DETAIL_EDIT,
} from "../../../../../components/dashboardcomponents/People/Groups/groupEvents";
import { GroupDetailPageSkeleton } from "../../../../../components/dashboardcomponents/People/Groups/GroupLoadingSkeletons";
import {
  archiveGroup,
  clearGroupDetail,
  fetchGroupById,
  updateGroup,
} from "../../../../../store/features/groupsApi";
import { buildGroupWritePayload } from "../../../../../utils/groupMappers";
import usePeoplePermissions from "../../../../../hooks/usePeoplePermissions";

function apiMessage(payload, fallback) {
  return payload?.message || fallback;
}

export default function GroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail, detailLoading, mutationLoading } = useSelector((state) => state.peopleGroups);
  const perms = usePeoplePermissions();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (groupId) dispatch(fetchGroupById({ groupId }));
    return () => dispatch(clearGroupDetail());
  }, [groupId, dispatch]);

  const group = useMemo(() => buildGroupDetailView(detail), [detail]);

  useEffect(() => {
    const onArchive = () => {
      if (perms.groups.canArchive) setArchiveOpen(true);
    };
    const onEdit = () => {
      if (perms.groups.canEdit) setEditOpen(true);
    };
    window.addEventListener(GROUP_DETAIL_ARCHIVE, onArchive);
    window.addEventListener(GROUP_DETAIL_EDIT, onEdit);
    return () => {
      window.removeEventListener(GROUP_DETAIL_ARCHIVE, onArchive);
      window.removeEventListener(GROUP_DETAIL_EDIT, onEdit);
    };
  }, [perms.groups.canArchive, perms.groups.canEdit]);

  if (detailLoading && !group) {
    return <GroupDetailPageSkeleton />;
  }

  if (!group) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "Inter", color: "#405261" }}>Group not found.</Typography>
      </Box>
    );
  }

  const handleArchiveConfirm = async () => {
    if (!groupId) return;
    const result = await dispatch(archiveGroup({ groupId }));
    if (archiveGroup.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, `${group.name} archived`));
      setArchiveOpen(false);
      navigate("/people/groups");
    } else {
      toast.error(apiMessage(result.payload, "Failed to archive group"));
    }
  };

  const handleEditConfirm = async (form) => {
    if (!groupId) return;
    const result = await dispatch(
      updateGroup({
        groupId,
        body: buildGroupWritePayload(form),
      })
    );
    if (updateGroup.fulfilled.match(result)) {
      toast.success(apiMessage(result.payload, "Group updated"));
      setEditOpen(false);
      dispatch(fetchGroupById({ groupId }));
    } else {
      toast.error(apiMessage(result.payload, "Failed to update group"));
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <GroupDetailHeaderBand group={group} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pt: "32px",
          px: 3,
          pb: 3,
        }}
      >
        <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
          <GroupDetailStatsRow stats={group.stats} />
          <GroupDetailInfoSection group={group} />
          <GroupDetailTeamsSection teams={group.teams} />
        </Box>
      </Box>

      <GroupArchiveModal
        open={archiveOpen}
        groupName={group.name}
        onClose={() => setArchiveOpen(false)}
        onConfirm={handleArchiveConfirm}
      />

      <GroupFormModal
        open={editOpen}
        mode="edit"
        initialValues={{ name: group.name, status: group.status }}
        onClose={() => setEditOpen(false)}
        onConfirm={handleEditConfirm}
        submitting={mutationLoading}
      />
    </Box>
  );
}
